import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useAssetJobRules, useCreateAssetJobRule, useUpdateAssetJobRule, useDeleteAssetJobRule } from '@/hooks/useAssetJobRules';
import { useAssets, ASSET_CATEGORY_LABELS } from '@/hooks/useAssets';
import { Plus, Trash2, Link, Unlink, Settings } from 'lucide-react';

const JOB_CATEGORIES = [
  { key: 'brakes', label: 'Brakes' },
  { key: 'suspension', label: 'Suspension' },
  { key: 'engine', label: 'Engine' },
  { key: 'transmission', label: 'Transmission' },
  { key: 'electrical', label: 'Electrical' },
  { key: 'ac', label: 'A/C & Climate' },
  { key: 'alignment', label: 'Alignment' },
  { key: 'tires', label: 'Tires & Wheels' },
  { key: 'diagnostic', label: 'Diagnostics' },
  { key: 'exhaust', label: 'Exhaust' },
  { key: 'maintenance', label: 'General Maintenance' },
];

const ASSET_CATEGORIES = Object.entries(ASSET_CATEGORY_LABELS).filter(
  ([key]) => !['lift_bay', 'flat_bay'].includes(key)
);

interface JobRulesManagerProps {
  /** When set, only show rules where asset_category matches OR rules tied to assets in this category. New rules created from here are pre-scoped. */
  assetCategoryFilter?: string;
}

/**
 * Job-to-Equipment Attribution Rules Manager
 * Allows Manager/Writer to configure which jobs attribute to which equipment
 */
export function JobRulesManager({ assetCategoryFilter }: JobRulesManagerProps = {}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [newRule, setNewRule] = useState<{
    job_category_key: string;
    asset_category: string;
    attribution_mode: 'auto' | 'manual';
  }>({
    job_category_key: '',
    asset_category: assetCategoryFilter ?? '',
    attribution_mode: 'auto',
  });

  const { data: allRules, isLoading } = useAssetJobRules();
  const { data: assets } = useAssets();
  const createRule = useCreateAssetJobRule();
  const updateRule = useUpdateAssetJobRule();
  const deleteRule = useDeleteAssetJobRule();

  const rules = assetCategoryFilter
    ? allRules?.filter((r) => r.asset_category === assetCategoryFilter)
    : allRules;

  const handleCreate = () => {
    if (!newRule.job_category_key || !newRule.asset_category) return;
    
    createRule.mutate({
      job_category_key: newRule.job_category_key,
      asset_category: newRule.asset_category,
      attribution_mode: newRule.attribution_mode,
    }, {
      onSuccess: () => {
        setCreateOpen(false);
        setNewRule({ job_category_key: '', asset_category: '', attribution_mode: 'auto' });
      },
    });
  };

  const handleToggleActive = (id: string, active: boolean) => {
    updateRule.mutate({ id, active: !active });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Attribution Rules
            </CardTitle>
            <CardDescription>
              Configure which job types automatically attribute time to equipment
            </CardDescription>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Add Rule
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Attribution Rule</DialogTitle>
                <DialogDescription>
                  Map a job category to equipment that should receive time attribution
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Job Category</Label>
                  <Select 
                    value={newRule.job_category_key}
                    onValueChange={(v) => setNewRule(p => ({ ...p, job_category_key: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select job category" />
                    </SelectTrigger>
                    <SelectContent>
                      {JOB_CATEGORIES.map(cat => (
                        <SelectItem key={cat.key} value={cat.key}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-center py-2">
                  <Link className="h-5 w-5 text-muted-foreground" />
                </div>
                
                <div className="space-y-2">
                  <Label>Equipment Category</Label>
                  <Select 
                    value={newRule.asset_category}
                    onValueChange={(v) => setNewRule(p => ({ ...p, asset_category: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select equipment category" />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_CATEGORIES.map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Attribution Mode</Label>
                  <Select 
                    value={newRule.attribution_mode}
                    onValueChange={(v) => setNewRule(p => ({ ...p, attribution_mode: v as 'auto' | 'manual' }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automatic</SelectItem>
                      <SelectItem value="manual">Manual confirmation</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Auto: Time attributed automatically when job completes. 
                    Manual: Requires user confirmation.
                  </p>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreate} 
                  disabled={!newRule.job_category_key || !newRule.asset_category || createRule.isPending}
                >
                  Create Rule
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      
      <CardContent>
        {!rules || rules.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Unlink className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No attribution rules configured</p>
            <p className="text-sm">Default rules based on job category keywords are used</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map(rule => {
              const jobLabel = JOB_CATEGORIES.find(c => c.key === rule.job_category_key)?.label || rule.job_category_key;
              const assetLabel = ASSET_CATEGORY_LABELS[rule.asset_category as keyof typeof ASSET_CATEGORY_LABELS] || rule.asset_category;
              
              return (
                <div 
                  key={rule.id} 
                  className={`flex items-center justify-between p-3 border rounded-lg ${!rule.active ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <Switch 
                      checked={rule.active}
                      onCheckedChange={() => handleToggleActive(rule.id, rule.active)}
                    />
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{jobLabel}</Badge>
                      <Link className="h-4 w-4 text-muted-foreground" />
                      <Badge variant="secondary">{assetLabel}</Badge>
                    </div>
                    {rule.attribution_mode === 'manual' && (
                      <Badge variant="outline" className="text-xs">Manual</Badge>
                    )}
                  </div>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Rule</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this attribution rule? 
                          Future jobs will no longer auto-attribute to this equipment.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => deleteRule.mutate(rule.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
